import { type GridState } from "ag-grid-community"

/**
 * A registered grid's record of the state it last applied (its initialState, then every
 * applyState), the columns that have had it, and the columns it had as of the last
 * `takeNewColumns`. Columns created later (async extradata) take their saved state from it; the
 * persisted state can't serve, since saves rewrite it from a grid that doesn't have those columns
 * yet.
 */
export type ColumnBook = {
  applied?: GridState
  took: Set<string>
  known: Set<string>
}

/** A book for a grid that applied `applied` to `colIds`. */
export const newColumnBook = (
  applied: GridState | undefined,
  colIds: Iterable<string>,
): ColumnBook => ({ applied, took: new Set(colIds), known: new Set(colIds) })

/**
 * The columns the grid gained since the last call. `late` ones are listed by the applied state,
 * accepted by `isLate` and haven't had that state yet; they take it, once. The rest are `fresh`.
 * Columns the grid dropped are forgotten, so one that comes back is gained again.
 */
export function takeNewColumns(
  book: ColumnBook,
  colIds: readonly string[],
  isLate: (colId: string) => boolean,
): { fresh: string[]; late?: { saved: GridState; colIds: string[] } } {
  const added = colIds.filter((colId) => !book.known.has(colId))
  // Nothing added and none dropped: the common columnDefs change.
  if (added.length === 0 && colIds.length === book.known.size) return { fresh: [] }
  const listed = new Set(book.applied?.columnOrder?.orderedColIds)
  const fresh: string[] = []
  const late: string[] = []
  for (const colId of added) {
    if (listed.has(colId) && !book.took.has(colId) && isLate(colId)) late.push(colId)
    else fresh.push(colId)
  }
  book.known = new Set(colIds)
  for (const colId of late) book.took.add(colId)
  return book.applied && late.length > 0
    ? { fresh, late: { saved: book.applied, colIds: late } }
    : { fresh }
}

/** Whether a grid state shows a column: `undefined` when it doesn't list it. */
export type StateVisibility = (colId: string) => boolean | undefined

/** {@link StateVisibility} of `state`. */
export function stateVisibility(state: GridState | undefined): StateVisibility {
  const listed = new Set(state?.columnOrder?.orderedColIds)
  const hidden = new Set(state?.columnVisibility?.hiddenColIds)
  return (colId) => (listed.has(colId) ? !hidden.has(colId) : undefined)
}
